/**
 * Quantos comentários cada ambiente tem — o balão com número das listas do
 * Legado. Uma request por página (`/ambientes/comentarios/contagem`), não uma
 * por linha: o resultado fica num cache de módulo compartilhado por todas as
 * tabelas montadas e é relido quando alguém comenta, edita ou apaga
 * (`recarregarContagemComentarios`) ou quando passa do prazo de validade.
 */
import { useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { contarComentariosAmbientes } from "../services/mapaProducao";

/** Mesmo gate do backend (`PERMS_HISTORICO_AMBIENTE`): quem vê qualquer página do Legado. */
export const PERMS_HISTORICO_AMBIENTE = [
  "mapa_producao:view",
  "comercial:view",
  "liberacao:view",
  "acabamentos:view",
  "medicao:view",
  "executivo:view",
  "aprovacao:view",
  "revisao:view",
  "ppcp:view",
  "compras:view",
  "ferragens:view",
  "industrializacao:view",
  "marcenaria:view",
  "terceiros:view",
  "acabamento:view",
  "entregas:view",
  "obras:view",
];

const VALIDADE_MS = 60_000;
const VAZIO = Object.freeze({});

const cache = { dono: null, totais: VAZIO, lidoEm: 0, emAndamento: null, request: null };
const ouvintes = new Set();

function avisar() {
  ouvintes.forEach((ouvinte) => ouvinte(cache.totais));
}

function buscar(request, dono) {
  if (cache.emAndamento && cache.dono === dono) return cache.emAndamento;
  cache.dono = dono;
  cache.request = request;
  cache.emAndamento = contarComentariosAmbientes(request)
    .then((totais) => {
      if (cache.dono !== dono) return;
      cache.totais = totais && typeof totais === "object" ? totais : VAZIO;
      cache.lidoEm = Date.now();
      avisar();
    })
    // Contagem é enfeite da lista: sem ela a tela continua funcionando, e o
    // histórico do painel mostra o próprio erro. Não repete em loop: a
    // próxima tentativa só depois da validade.
    .catch(() => {
      cache.lidoEm = Date.now();
    })
    .finally(() => {
      cache.emAndamento = null;
    });
  return cache.emAndamento;
}

/** Releitura imediata depois de comentar/editar/apagar. */
export function recarregarContagemComentarios() {
  if (!cache.request) return Promise.resolve();
  cache.lidoEm = 0;
  return buscar(cache.request, cache.dono);
}

/** `{ [chave]: total }` (objeto vazio enquanto carrega ou sem permissão). */
export default function useContagemComentarios({ ativo = true } = {}) {
  // O balão é enfeite da lista: fora de uma sessão completa (sem usuário ou
  // sem o checador de permissão) ele simplesmente não aparece.
  const { request, usuario, hasAnyPermission } = useAuth() || {};
  const permitido =
    ativo &&
    Boolean(usuario) &&
    typeof hasAnyPermission === "function" &&
    hasAnyPermission(PERMS_HISTORICO_AMBIENTE);
  const dono = usuario?.id ?? null;
  const [totais, setTotais] = useState(() => (cache.dono === dono ? cache.totais : VAZIO));

  useEffect(() => {
    if (!permitido) return undefined;
    ouvintes.add(setTotais);
    if (cache.dono !== dono) {
      cache.totais = VAZIO;
      cache.lidoEm = 0;
    }
    setTotais(cache.totais);
    if (Date.now() - cache.lidoEm > VALIDADE_MS) buscar(request, dono);
    return () => {
      ouvintes.delete(setTotais);
    };
  }, [permitido, request, dono]);

  return permitido ? totais : VAZIO;
}
